/**
 * Kippush global motion system — single shared scroll-reveal observer.
 *
 * Any element anywhere in the theme can opt in with [data-kp-reveal]
 * (styled in assets/base.css) and an optional inline
 * style="--kp-reveal-index:N" for stagger. This script is the only place
 * that logic lives — sections must not re-implement their own inline
 * copy of it (see CLAUDE.md's Kippush interaction system rule).
 *
 * Fails open in every direction: prefers-reduced-motion or a missing
 * IntersectionObserver reveal everything immediately, and a safety
 * timeout force-reveals anything left over so content can never get
 * stuck invisible if observation never fires (e.g. a backgrounded tab).
 */
(function () {
  var SAFETY_TIMEOUT_MS = 4000;
  var STAGGER_MS = 80;
  var THRESHOLD = 0.2;
  var BOUND_ATTR = 'kpRevealBound';

  // A handful of sections are built from native Shopify blocks (group/
  // icon/text) rendered by the theme's own generic block engine, not a
  // custom .liquid file we author — so there is no markup-injection
  // point to add [data-kp-reveal] directly. Native blocks still render
  // stable, block-id-derived class/id hooks (see CLAUDE.md), so those
  // elements opt into the identical reveal treatment here instead. Keep
  // this list short: [data-kp-reveal] on markup we do control remains
  // the default, preferred way to opt in everywhere else.
  //
  // Selectors use [id$="__handle"] (ends-with), never a bare
  // #shopify-section-<handle> id — Shopify renders section wrapper ids
  // as shopify-section-template--<n>__<handle> (a per-template numeric
  // prefix), so an exact-id selector never matches in real storefront
  // rendering. This was a real, shipped bug (2026-09-02 QA correction)
  // fixed everywhere it appeared — see CLAUDE.md.
  var NATIVE_REVEAL_TARGETS = [
    { selector: '[id$="__section_tqxWe7"] .group-block:has([id*="__icon_wE8tKh"])', index: 0 },
    { selector: '[id$="__section_tqxWe7"] .group-block:has([id*="__icon_nHm6tQ"])', index: 0.6 },
    { selector: '[id$="__section_tqxWe7"] .group-block:has([id*="__icon_86rfr4"])', index: 1.2 },
    // Homepage FAQ (native "accordion" section, no custom .liquid file to
    // add [data-kp-reveal] to directly) — single reveal on the whole
    // section wrapper, no per-question stagger.
    { selector: '[id$="__section_6zgp6D"]', index: 0 },
  ];

  function reveal(el) {
    el.classList.add('is-kp-visible');
  }

  function revealAll(items) {
    items.forEach(reveal);
  }

  function collectNativeTargets() {
    var found = [];
    if (!('querySelector' in document) || typeof CSS === 'undefined' || !CSS.supports || !CSS.supports('selector(:has(a))')) {
      return found; // :has() unsupported — these stay statically visible, never hidden
    }
    NATIVE_REVEAL_TARGETS.forEach(function (cfg) {
      var el;
      try {
        el = document.querySelector(cfg.selector);
      } catch (e) {
        el = null;
      }
      if (el && !el.dataset[BOUND_ATTR]) {
        el.style.setProperty('--kp-reveal-index', cfg.index);
        el.classList.add('kp-reveal-native');
        found.push(el);
      }
    });
    return found;
  }

  function scan() {
    var items = Array.prototype.slice
      .call(document.querySelectorAll('[data-kp-reveal]'))
      .concat(collectNativeTargets())
      .filter(function (el) {
        return !el.dataset[BOUND_ATTR];
      });

    if (!items.length) return;

    items.forEach(function (el) {
      el.dataset[BOUND_ATTR] = 'true';
    });

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealAll(items);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var idx = parseFloat(el.style.getPropertyValue('--kp-reveal-index')) || 0;
          setTimeout(function () {
            reveal(el);
          }, idx * STAGGER_MS);
          observer.unobserve(el);
        });
      },
      { threshold: THRESHOLD }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });

    setTimeout(function () {
      revealAll(items);
    }, SAFETY_TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Re-scan when the Theme Editor swaps in new/edited sections so newly
  // added [data-kp-reveal] elements still animate in.
  document.addEventListener('shopify:section:load', scan);
})();

/**
 * Kippush PDP pricing-tier highlight — reacts to the theme's real
 * quantity-change event, not native `input`.
 *
 * 2026-09-08 fix: the quantity stepper's +/- buttons
 * (component-quantity-selector.js) set the input's `.value` directly via
 * JS, which never fires a native `input` event in any browser — only a
 * genuine keystroke does that. The component instead broadcasts its own
 * `quantity-selector:update` CustomEvent (ThemeEvents.quantitySelectorUpdate,
 * see assets/events.js) on every real quantity change, button click and
 * blur-commit alike. Each per-template pricing script previously listened
 * for native `input`, so the highlighted tier only ever tracked a value
 * typed by hand before blur — clicking +/- left it stuck on whatever
 * tier was active on page load.
 *
 * This is now the one shared place that sync logic lives. Tier
 * boundaries are NOT shared across products — richtext ("text"-type)
 * blocks strip custom data-* attributes on save (see CLAUDE.md), so
 * tiers can't be tagged directly on the <li> markup — each PDP template
 * still declares its own business rules by pushing a small config to
 * window.kpPricingTiers (index-based row matching, with an optional
 * `offset` for tables that render a header row first). See
 * templates/product.json, product.white-satin-kippa.json and
 * product.white-shawl.json for the per-template registration.
 */
(function () {
  window.kpPricingTiers = window.kpPricingTiers || [];

  function syncPricingTiers(quantity) {
    window.kpPricingTiers.forEach(function (config) {
      document.querySelectorAll(config.selector).forEach(function (list) {
        var items = list.querySelectorAll('li');
        var offset = config.offset || 0;
        items.forEach(function (li, i) {
          var tier = config.tiers[i - offset];
          li.classList.toggle('is-active-tier', !!tier && quantity >= tier.min && quantity <= tier.max);
        });
      });
    });
  }

  function syncAllPricingTiers() {
    document.querySelectorAll('input[name="quantity"]').forEach(function (input) {
      var value = parseInt(input.value, 10);
      if (!isNaN(value)) syncPricingTiers(value);
    });
  }

  document.addEventListener('quantity-selector:update', function (event) {
    if (event.detail && typeof event.detail.quantity === 'number') {
      syncPricingTiers(event.detail.quantity);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllPricingTiers);
  } else {
    syncAllPricingTiers();
  }

  document.addEventListener('shopify:section:load', syncAllPricingTiers);
})();
